import { ChatChip, CAPABILITIES_PROMPT } from '../work/ChipManager';

export const ADMIN_CHIP_STORAGE_KEY = 'tpp-admin-chat-chips';

export const DEFAULT_ADMIN_CHIPS: ChatChip[] = [
  { id: 'admin-capabilities', label: '🎭 Capabilities', prompt: CAPABILITIES_PROMPT, isDefault: true },
  { id: 'admin-users', label: '👥 All Users', prompt: 'List all users with their roles, status (active/disabled), auth provider, and last login date. Highlight any disabled accounts or accounts with no recent activity.', isDefault: true },
  { id: 'admin-create-user', label: '➕ Create User', prompt: 'Help me create a new user account. Ask me for their email, username, and role (admin/editor/viewer).', isDefault: true },
  { id: 'admin-stats', label: '📊 System Stats', prompt: `Show me comprehensive platform statistics. Call admin_get_system_stats and admin_count_entities. Present:

1. **Platform Health** — total users, active sessions, personas, artifacts
2. **Entity Counts** — every entity type with count, presented as a clean table
3. **Usage Metrics** — token usage, audit entries in last 24h
4. **Growth** — any notable changes or trends

Include a mermaid pie chart showing entity distribution across the platform.`, isDefault: true },
  { id: 'admin-audit', label: '🔍 Audit Log', prompt: `Show the audit log for the last 24 hours. Call admin_view_audit_log. Present:

1. **Activity Summary** — total actions, unique users, most active user
2. **Actions by Type** — a mermaid pie chart showing create/update/delete/login distribution
3. **Detailed Log** — chronological list with timestamp, user, action, resource type, and details
4. **Anomalies** — flag any unusual patterns (many deletes, failed logins, off-hours activity)`, isDefault: true },
  { id: 'admin-version', label: '📦 Version', prompt: 'Show me the current platform version, release notes, and any available updates. Include the tech stack versions if available.', isDefault: true },
  { id: 'admin-roles', label: '🔐 RBAC Overview', prompt: `Show me the RBAC configuration. Call admin_get_platform_info. Present:

1. **Roles** — each role with its permission count and scope
2. **Permission Matrix** — entity types vs actions, showing which roles have which permissions
3. **User Distribution** — a mermaid pie chart showing how many users are in each role
4. **Security Notes** — any roles with admin/execute permissions that should be reviewed`, isDefault: true },
  { id: 'admin-work-dashboard', label: '📋 Work Dashboard', prompt: `Give me a comprehensive executive work dashboard. Call admin_work_summary, admin_work_attention, and admin_standup_summary. Present:

1. **Health Score** — prominently with assessment
2. **Status Distribution** — mermaid pie chart of all task statuses
3. **Velocity** — this week vs last week with trend
4. **Risk Board** — overdue + blocked + unassigned critical
5. **Project Health** — mermaid gantt of active projects
6. **Upcoming Deadlines** — next 7 days table`, isDefault: true },
  { id: 'admin-invites', label: '✉️ Invites', prompt: 'Show me all pending user invitations. List each with email, invited date, and status. Also show recently accepted invites.', isDefault: true },
  { id: 'admin-heartbeat', label: '💓 Heartbeat', prompt: `First call list_pulses to show the current heartbeat configuration (tier, schedule, persona, model).

Then run a heartbeat pulse: call run_pulse with pulse_type="heartbeat" and date_range_days=1.

After it completes, show:
1. **Briefing Title** — the narrator's chosen title
2. **Full Content** — the complete briefing text as generated
3. **Token Usage** — prompt tokens, completion tokens, total, estimated cost
4. **Tier** — which tier was used

Finally, remind me I can change tier, schedule, or persona anytime using update_pulse_config.`, isDefault: true },
];

export function loadAdminChips(): ChatChip[] {
  try {
    const stored = localStorage.getItem(ADMIN_CHIP_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_ADMIN_CHIPS.map(c => ({ ...c }));
}

export function saveAdminChips(chips: ChatChip[]) {
  localStorage.setItem(ADMIN_CHIP_STORAGE_KEY, JSON.stringify(chips));
}
